/**
 * Pending Plan Change Card - Trade Loop v2.0
 *
 * Displays information about a scheduled plan change
 * - Shows current plan → new plan
 * - Shows effective date
 * - Allows canceling the pending change
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, ArrowRight, X, AlertCircle } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';

interface PendingPlanChangeCardProps {
  planChangeId: string;
  currentPlan: string;
  newPlan: string;
  effectiveDate: Date;
  onCancel?: () => void;
}

export function PendingPlanChangeCard({
  planChangeId,
  currentPlan,
  newPlan,
  effectiveDate,
  onCancel,
}: PendingPlanChangeCardProps) {
  const [isCanceling, setIsCanceling] = useState(false);
  const { showSuccess, showError } = useToast();

  const currentPlanName = currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1);
  const newPlanName = newPlan.charAt(0).toUpperCase() + newPlan.slice(1);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const handleCancelChange = async () => {
    try {
      setIsCanceling(true);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/workflows/subscriptions/plan-change/${planChangeId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to cancel plan change';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      showSuccess('Plan Change Canceled', 'Your scheduled plan change has been canceled');

      // Call onCancel callback to refresh parent data
      if (onCancel) {
        onCancel();
      }
    } catch (error: any) {
      console.error('Cancel plan change error:', error);
      showError('Failed to Cancel', error.message || 'Unable to cancel plan change at this time');
    } finally {
      setIsCanceling(false);
    }
  };

  return (
    <Card className="border-2 border-amber-200 bg-amber-50">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5 text-amber-600" />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <h3 className="font-semibold text-amber-900 text-sm">Scheduled Plan Change</h3>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center px-2 py-1 rounded bg-white border border-amber-200 text-sm font-medium text-amber-900">
                {currentPlanName}
              </span>
              <ArrowRight className="w-4 h-4 text-amber-600" />
              <span className="inline-flex items-center px-2 py-1 rounded bg-white border border-amber-200 text-sm font-medium text-amber-900">
                {newPlanName}
              </span>
            </div>

            <p className="text-sm text-amber-800 mb-3">
              Your subscription will change to <strong>{newPlanName}</strong> on{' '}
              <strong>{formatDate(effectiveDate)}</strong>. You'll keep all{' '}
              <strong>{currentPlanName}</strong> features until then.
            </p>

            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelChange}
              disabled={isCanceling}
              className="text-amber-700 hover:text-amber-800 hover:bg-amber-100 border-amber-300"
            >
              <X className="w-4 h-4 mr-1.5" />
              {isCanceling ? 'Canceling...' : 'Cancel Plan Change'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
